import React, { useMemo, useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Grid,
  Typography,
  TextField,
  Button,
  Paper,
  Stack,
  Alert,
  Link,
  Box,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import {
  useApiGet,
  useApiSend,
  getApiErrorMessage,
} from '../../utils/httpClientTs';
import { getFinanceFundsList, createFund } from '../../endpoints/financeFunds';
import type { CreateFundRequest } from '../../types/financeFunds';

const FundCreate: React.FC = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [accountId, setAccountId] = useState('');
  const [shortName, setShortName] = useState('');
  const [canDonate, setCanDonate] = useState(true);
  const [establishedBy, setEstablishedBy] = useState('');
  const [establishedDate, setEstablishedDate] = useState<Dayjs | null>(dayjs());
  const [formError, setFormError] = useState<string | null>(null);

  const listParams = useMemo(
    () => ({ page: 1, page_size: 1, order_by: 'name', order: 'asc' as const }),
    []
  );

  const {
    data: listMeta,
    isLoading: metaLoading,
    isError: metaError,
  } = useApiGet(
    ['finance-funds', 'can-create-check'],
    () => getFinanceFundsList(listParams),
    { enabled: true, retry: 1 }
  );

  const { mutate: submitCreate, isPending } = useApiSend(
    (body: CreateFundRequest) => createFund(body),
    (res) => {
      navigate(`/finance/funds/${res.id}`);
    },
    (err) => {
      setFormError(getApiErrorMessage(err));
    },
    [['finance-funds']]
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!name.trim()) {
      setFormError('Name is required.');
      return;
    }
    if (!accountId.trim()) {
      setFormError('Account is required.');
      return;
    }
    if (!shortName.trim()) {
      setFormError('Short name is required.');
      return;
    }
    if (!establishedBy.trim()) {
      setFormError('Established by is required.');
      return;
    }
    if (!establishedDate) {
      setFormError('Established date is required.');
      return;
    }
    const body: CreateFundRequest = {
      name: name.trim(),
      account_id: accountId.trim(),
      short_name: shortName.trim(),
      can_donate: canDonate ? 1 : 0,
      established_by: establishedBy.trim(),
      established_date: establishedDate.format('YYYY-MM-DD'),
    };
    submitCreate(body);
  };

  const forbidden = !metaLoading && listMeta && listMeta.can_create === false;

  return (
    <Grid container paddingLeft={2} paddingRight={2}>
      <Grid item xs={12} padding={1}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/finance/funds')}
            size="small"
          >
            Back to funds
          </Button>
          <Typography variant="h4" color="primary">
            Create fund
          </Typography>
        </Stack>
      </Grid>

      {metaLoading && (
        <Grid item xs={12}>
          <Typography>Loading…</Typography>
        </Grid>
      )}

      {metaError && !metaLoading && (
        <Grid item xs={12}>
          <Alert severity="error" sx={{ mb: 2 }}>
            Could not load permissions.{' '}
            <Link component={RouterLink} to="/finance/funds">
              Return to funds
            </Link>
          </Alert>
        </Grid>
      )}

      {forbidden && (
        <Grid item xs={12}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            You do not have permission to create funds. International{' '}
            <strong>funds:manage</strong> is required.{' '}
            <Link component={RouterLink} to="/finance/funds">
              Return to funds
            </Link>
          </Alert>
        </Grid>
      )}

      {!metaLoading && !metaError && !forbidden && (
        <Grid item xs={12} md={8} lg={6}>
          <Paper sx={{ p: 3 }} component="form" onSubmit={handleSubmit}>
            {formError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {formError}
              </Alert>
            )}
            <Stack spacing={2}>
              <TextField
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                fullWidth
                size="small"
                id="fund-create-name"
                inputProps={{ maxLength: 100 }}
              />
              <TextField
                label="Account"
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                required
                fullWidth
                size="small"
                id="fund-create-account"
                inputProps={{ maxLength: 32 }}
              />
              <TextField
                label="Short name"
                value={shortName}
                onChange={(e) => setShortName(e.target.value)}
                required
                fullWidth
                size="small"
                id="fund-create-short-name"
                inputProps={{ maxLength: 26 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={canDonate}
                    onChange={(e) => setCanDonate(e.target.checked)}
                    id="fund-create-can-donate"
                  />
                }
                label="Can donate"
              />
              <TextField
                label="Established by"
                value={establishedBy}
                onChange={(e) => setEstablishedBy(e.target.value)}
                required
                fullWidth
                size="small"
                id="fund-create-established-by"
                inputProps={{ maxLength: 255 }}
              />
              <Box>
                <DatePicker
                  label="Established date"
                  value={establishedDate}
                  onChange={(v) => setEstablishedDate(v)}
                  maxDate={dayjs()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      required: true,
                    },
                  }}
                />
              </Box>
              <Stack direction="row" spacing={2}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isPending}
                >
                  Create
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => navigate('/finance/funds')}
                  disabled={isPending}
                >
                  Cancel
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Grid>
      )}
    </Grid>
  );
};

export default FundCreate;
